import { Module } from 'vuex';

import { Collection, CollectionName } from '@/services/collections/collections.types';
import { MatchError } from '@/services/match-errors/match-errors.types';
import { RootState } from '@/store';
import { Purchase } from '@/services/purchases/purchases.types';

export interface FocusState {
  modal:
    | 'CageInfosModal'
    | 'CommissionInfoModal'
    | 'DeviceInfosModal'
    | 'MatchInfosModal'
    | 'MarketplaceInfosModal'
    | 'OrderInfosModal'
    | 'OrderLineInfosModal'
    | 'OrderReturnInfosModal'
    | 'PartInfosModal'
    | 'PurchaseInfosModal'
    | 'ProductionOrderInfosModal'
    | 'PickingInfosModal'
    | 'SupplierInfosModal'
    | 'BuybackInfosModal'
    | 'OrderSparePartInfosModal'
    | 'InvoiceInfosModal'
    | 'PaymentLinkInfosModal'
    | 'AccountingMappingInfosModal'
    | 'TaxAccountingEntryInfosModal'
    | 'CustomerInfosModal'
    | 'EntryFlowStatsModal';
}

const state: FocusState = {
  modal: null
};

const options: Module<FocusState, RootState> = {
  namespaced: true,
  state: () => state,
  actions: {
    setFocus<T extends CollectionName>(
      { dispatch, commit },
      {
        table,
        item,
        initTab,
        onClose,
        extraProps
      }: {
        table: T;
        item: Collection<T> | string | number;
        initTab?: string;
        onClose: (isConfirmed?: boolean) => Promise<any>;
        /* Props libres transmises telles quelles au composant modal (ex: restaurer un onglet/filtres au retour) */
        extraProps?: Record<string, unknown>;
      }
    ): Promise<void> {
      return dispatch('clearFocus')
        .then(() => {
          /**
           * For the modal name, we cannot use a simple getter based on table, because when table === 'devices',
           * sometimes we use the Device modal and sometimes we use the Match modal (when it does have a Match).
           */
          if (table === 'devices') {
            commit('modal', 'DeviceInfosModal');
            return item && dispatch('device/setDevice', item, { root: true });
          } else if (table === 'orders') {
            commit('modal', 'OrderInfosModal');
            return item && dispatch('order/setOrder', item, { root: true });
          } else if (table === 'order-lines') {
            commit('modal', 'OrderLineInfosModal');
            return item && dispatch('order-line/setOrderLine', item, { root: true });
          } else if (table === 'order-returns') {
            commit('modal', 'OrderReturnInfosModal');
            return item && dispatch('order-return/setOrderReturn', item, { root: true });
          } else if (table === 'production-orders') {
            commit('modal', 'ProductionOrderInfosModal');
            return item && dispatch('production-order/setProductionOrder', item, { root: true });
          } else if (table === 'pickings') {
            commit('modal', 'PickingInfosModal');
            return item && dispatch('picking/setPicking', item, { root: true });
          } else if (table === 'matches') {
            commit('modal', 'MatchInfosModal');
            return item && dispatch('match/setMatch', item, { root: true });
          } else if (table === 'match-errors') {
            /* We use the same modal as for the Match, so we need to use the match id so set the Match store */
            commit('modal', 'MatchInfosModal');
            const matchId = (item as MatchError).match.id;
            return item && dispatch('match/setMatch', matchId, { root: true });
          } else if (table === 'dispatch-cages') {
            commit('modal', 'CageInfosModal');
            return item && dispatch('cage/setCage', item, { root: true });
          } else if (table === 'commissions') {
            commit('modal', 'CommissionInfosModal');
            return item && dispatch('commission/setCommission', item, { root: true });
          } else if (table === 'purchases') {
            commit('modal', 'PurchaseInfosModal');
            const purchaseId = (item as Purchase).id;
            item && dispatch('purchase/getPurchase', purchaseId, { root: true });
            return item && dispatch('purchase/setPurchase', item, { root: true });
          } else if (table === 'parts') {
            commit('modal', 'PartInfosModal');
            return item && dispatch('part/setPart', item, { root: true });
          } else if (table === 'special-process-priority') {
            commit('modal', 'DeviceInfosModal');
            return item && dispatch('device/setDevice', item, { root: true });
          } else if (table === 'suppliers') {
            commit('modal', 'SupplierInfosModal');
            return dispatch('supplier/setSupplier', item, { root: true });
          } else if (table === 'marketplaces') {
            commit('modal', 'MarketplaceInfosModal');
            return item && dispatch('marketplace/setMarketplace', item, { root: true });
          } else if (table === 'buybacks') {
            commit('modal', 'BuybackInfosModal');
            return item && dispatch('buyback/setBuyback', item, { root: true });
          } else if (table === 'order-spare-parts') {
            commit('modal', 'OrderSparePartInfosModal');
            return item && dispatch('order-spare-part/setOrderSparePart', item, { root: true });
          } else if (table === 'invoices') {
            commit('modal', 'InvoiceInfosModal');
            return item && dispatch('invoice/setInvoice', item, { root: true });
          } else if (table === 'payment-links') {
            commit('modal', 'PaymentLinkInfosModal');
            return item && dispatch('payment-link/setPaymentLink', item, { root: true });
          } else if (table === 'accounting-mappings') {
            commit('modal', 'AccountingMappingInfosModal');
            return item && dispatch('accounting-mapping/setAccountingMapping', item, { root: true });
          } else if (table === 'tax-accounting-entries') {
            commit('modal', 'TaxAccountingEntryInfosModal');
            return item && dispatch('tax-accounting-entry/setTaxAccountingEntry', item, { root: true });
          } else if (table === 'customers') {
            commit('modal', 'CustomerInfosModal');
            return item && dispatch('customer/setCustomer', item, { root: true });
          } else if (table === 'entry-flow-stats') {
            commit('modal', 'EntryFlowStatsModal');
            return item && dispatch('entry-flow-stats/setEntryFlowStats', item, { root: true });
          } else if (table === 'uk-transfer') commit('modal', 'DeviceInfosModal');
          return item && dispatch('device/setDevice', item, { root: true });
        })
        .then(() => {
          dispatch(
            'modal/showModal',
            { component: state.modal, ...(initTab && { initTab }), ...extraProps, action: 'edit', onClose },
            { root: true }
          );
        });
    },
    setCreationFocus<T extends CollectionName>(
      { dispatch, commit },
      {
        table,
        data,
        onClose,
        extraProps
      }: {
        table: T;
        data: T | number;
        onClose: (isConfirmed?: boolean) => Promise<any>;
        /* Props libres transmises telles quelles au dispatch de création (ex: is_bundle de la commande parente) */
        extraProps?: Record<string, unknown>;
      }
    ): Promise<void> {
      return dispatch('clearFocus')
        .then(() => {
          /**
           * If we ever need to create other types or resources, we should follow the same strategy as Order example
           */
          if (table === 'orders') {
            commit('modal', 'OrderCreationModal');
            return dispatch('order/newOrder', null, { root: true });
          } else if (table === 'order-lines') {
            commit('modal', 'OrderLineCreationModal');
            return (
              data &&
              dispatch(
                'order-line/newOrderLine',
                { orderId: data, orderIsBundle: !!extraProps?.orderIsBundle },
                { root: true }
              )
            );
          } else if (table === 'production-orders') {
            commit('modal', 'ProductionOrderCreationModal');
            return dispatch('production-order/newProductionOrder', null, { root: true });
          } else if (table === 'pickings') {
            commit('modal', 'PickingCreationModal');
            return dispatch('picking/newPicking', null, { root: true });
          } else if (table === 'devices') {
            commit('modal', 'DeviceCreationModal');
            return dispatch('device/newDevice', null, { root: true });
          } else if (table === 'purchases') {
            commit('modal', 'PurchaseCreationModal');
            return dispatch('purchase/newPurchase', null, { root: true });
          } else if (table === 'dispatch-cages') {
            commit('modal', 'CageCreationModal');
            return dispatch('cage/newCage', null, { root: true });
          } else if (table === 'commissions') {
            commit('modal', 'CommissionCreationModal');
            return dispatch('commission/newCommission', null, { root: true });
          } else if (table === 'suppliers') {
            commit('modal', 'SupplierCreationModal');
            return dispatch('supplier/newSupplier', null, { root: true });
          } else if (table === 'accounting-mappings') {
            commit('modal', 'AccountingMappingInfosModal');
            return dispatch('accounting-mapping/newAccountingMapping', null, { root: true });
          } else if (table === 'tax-accounting-entries') {
            commit('modal', 'TaxAccountingEntryInfosModal');
            return dispatch('tax-accounting-entry/newTaxAccountingEntry', null, { root: true });
          } else if (table === 'customers') {
            commit('modal', 'CustomerInfosModal');
            return dispatch('customer/newCustomer', null, { root: true });
          }
        })
        .then(() =>
          dispatch('modal/showModal', { component: state.modal, action: 'creation', onClose }, { root: true })
        );
    },
    clearFocus({ state, commit, dispatch }): Promise<void> {
      if (!state.modal) return;
      return dispatch('modal/hideModal', null, { root: true })
        .then(() => {
          if (state.modal === 'DeviceInfosModal') return dispatch('device/setDevice', null, { root: true });
          else if (state.modal === 'OrderInfosModal') return dispatch('order/setOrder', null, { root: true });
          else if (state.modal === 'OrderLineInfosModal')
            return dispatch('order-line/setOrderLine', null, { root: true });
          else if (state.modal === 'ProductionOrderInfosModal')
            return dispatch('production-order/setProductionOrder', null, { root: true });
          else if (state.modal === 'PickingInfosModal') return dispatch('picking/setPicking', null, { root: true });
          else if (state.modal === 'MatchInfosModal') return dispatch('match/setMatch', null, { root: true });
          else if (state.modal === 'CageInfosModal') return dispatch('cage/setCage', null, { root: true });
          else if (state.modal === 'CommissionInfoModal')
            return dispatch('commission/setCommission', null, { root: true });
          else if (state.modal === 'MarketplaceInfosModal')
            return dispatch('marketplace/setMarketplace', null, { root: true });
          else if (state.modal === 'PartInfosModal') return dispatch('part/setPart', null, { root: true });
          else if (state.modal === 'PurchaseInfosModal') return dispatch('purchase/setPurchase', null, { root: true });
          else if (state.modal === 'SupplierInfosModal') return dispatch('supplier/setSupplier', null, { root: true });
          else if (state.modal === 'BuybackInfosModal') return dispatch('buyback/setBuyback', null, { root: true });
          else if (state.modal === 'OrderSparePartInfosModal')
            return dispatch('order-spare-part/setOrderSparePart', null, { root: true });
          else if (state.modal === 'InvoiceInfosModal') return dispatch('invoice/setInvoice', null, { root: true });
          else if (state.modal === 'PaymentLinkInfosModal')
            return dispatch('payment-link/setPaymentLink', null, { root: true });
          else if (state.modal === 'AccountingMappingInfosModal')
            return dispatch('accounting-mapping/clearAccountingMapping', null, { root: true });
          else if (state.modal === 'TaxAccountingEntryInfosModal')
            return dispatch('tax-accounting-entry/clearTaxAccountingEntry', null, { root: true });
          else if (state.modal === 'CustomerInfosModal')
            return dispatch('customer/clearCustomer', null, { root: true });
          else if (state.modal === 'EntryFlowStatsModal')
            return dispatch('entry-flow-stats/clearEntryFlowStats', null, { root: true });
        })
        .then(() => commit('modal', null));
    }
  },
  mutations: {
    modal: (state, modal) => (state.modal = modal)
  }
};

export default options;
